import { computed, ref, type Ref } from 'vue'
import { wpFetchWithCtx, type PFCAdminContext } from '../../data/wpApi'
import { playerResetUrl } from '../model/restUrls'

/**
 * Exposed state of the embedded DeckPlayerShell. defineExpose unwraps refs, so these
 * are plain values rather than refs.
 */
type PreviewShellExposed = {
  graphRegisters?: Record<string, number>
  graphActivePhaseId?: string | null
  graphActiveGroupId?: string | null
  graphDoneCardIds?: Set<string>
  debugState?: {
    deckSize: number
    deckDone: number
    deckPoolRemaining: number
    groupSize: number
    groupPoolRemaining: number
    groupDone: number
    progressTotal: number | null
    progressCurrent: number | null
  }
}
export type PreviewHostExposed = {
  stepForward?: () => void
  shell?: PreviewShellExposed | null
}

/**
 * Live preview of the edited graph in a real deck player: step/play/pause controls,
 * the node under the cursor and the player's debug state.
 */
export function useFlowPreview(wpCtx: Ref<PFCAdminContext | null>, t: (key: string) => string) {
  /** Changing the key remounts the player, which restarts the session. */
  const previewSessionKey = ref(0)
  const previewActiveCursorNodeId = ref<string | null>(null)
  const previewStepSignal = ref(0)
  const previewNeedsUserInteraction = ref(false)
  const previewHostRef = ref<PreviewHostExposed | null>(null)
  const previewPlaying = ref(false)
  const previewPaused = ref(false)
  const previewSessionStatus = ref<string | null>(null)
  /** Delay between nodes while playing, in ms (minimum 25). */
  const debugPlayDelay = ref(25)

  const previewActiveNodeIds = computed(() => {
    const id = previewActiveCursorNodeId.value
    return id ? new Set<string>([id]) : new Set<string>()
  })

  const shell = computed(() => previewHostRef.value?.shell ?? null)
  const previewRegisters = computed(() => shell.value?.graphRegisters ?? { R1: 0, R2: 0, R3: 0 })
  const previewPhaseId = computed(() => shell.value?.graphActivePhaseId ?? null)
  const previewGroupId = computed(() => shell.value?.graphActiveGroupId ?? null)
  const previewDebugState = computed(() => shell.value?.debugState ?? null)

  const handlePreviewStepChanged = (payload: {
    nodeId: string | null
    needsUserInteraction?: boolean
  }) => {
    previewActiveCursorNodeId.value = payload.nodeId
    previewNeedsUserInteraction.value = payload.needsUserInteraction === true
  }

  const stepPreview = () => {
    if (previewNeedsUserInteraction.value) return
    if (previewHostRef.value?.stepForward) {
      previewHostRef.value.stepForward()
      return
    }
    previewStepSignal.value += 1
  }

  const restartPreview = (playing = false) => {
    previewActiveCursorNodeId.value = null
    previewNeedsUserInteraction.value = false
    previewStepSignal.value = 0
    previewPlaying.value = playing
    previewPaused.value = false
    previewSessionKey.value += 1
    previewSessionStatus.value = null
  }

  const pausePreview = () => {
    previewPlaying.value = false
    previewPaused.value = true
  }

  const continuePreview = () => {
    previewPaused.value = false
    previewPlaying.value = true
  }

  /** Deletes the stored flowchart session for this deck, then restarts the preview. */
  const clearBackendSession = () => {
    const ctx = wpCtx.value
    if (!ctx) return
    previewSessionStatus.value = t('flow.clearingSession')
    void wpFetchWithCtx(ctx, playerResetUrl(ctx), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: 'flowchart' }),
    })
      .then(() => {
        previewSessionStatus.value = t('flow.sessionCleared')
        restartPreview(previewPlaying.value)
      })
      .catch(() => {
        previewSessionStatus.value = t('flow.clearFailed')
      })
  }

  return {
    previewSessionKey,
    previewActiveCursorNodeId,
    previewActiveNodeIds,
    previewStepSignal,
    previewNeedsUserInteraction,
    previewHostRef,
    previewPlaying,
    previewPaused,
    previewSessionStatus,
    debugPlayDelay,
    previewRegisters,
    previewPhaseId,
    previewGroupId,
    previewDebugState,
    handlePreviewStepChanged,
    stepPreview,
    restartPreview,
    pausePreview,
    continuePreview,
    clearBackendSession,
  }
}

export type FlowPreview = ReturnType<typeof useFlowPreview>
